import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faBolt,
  faBriefcase,
  faBuilding,
  faCreditCard,
  faFileShield,
  faGlobe,
  faHandHoldingDollar,
  faKey,
  faLayerGroup,
  faRotate,
  faServer,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import { Tone } from "../components/ui/tones";
import { HealthStatus, SureProjectId } from "../types/log";
import { RunStatus } from "../types/job-run";
import { CheckStatus } from "../types/health-check";

export const PLATFORM_LOOK: Record<SureProjectId, { label: string; icon: IconDefinition; tone: Tone }> = {
  docusure: { label: "DocuSure", icon: faFileShield, tone: "sky" },
  debtsure: { label: "DebtSure", icon: faHandHoldingDollar, tone: "teal" },
  rensure: { label: "RenSure", icon: faRotate, tone: "violet" },
  "rensure-sme": { label: "RenSure SME", icon: faBuilding, tone: "pink" },
  premsure: { label: "PremSure", icon: faUserShield, tone: "rose" },
  "hq-sme": { label: "HQ-SME", icon: faBriefcase, tone: "orange" },
};

export const PLATFORM_IDS = Object.keys(PLATFORM_LOOK) as SureProjectId[];

export const platformLabel = (id: string) => PLATFORM_LOOK[id as SureProjectId]?.label ?? id;

export const CATEGORY_LOOK: Record<string, { label: string; icon: IconDefinition; tone: Tone }> = {
  payment: { label: "Payment", icon: faCreditCard, tone: "amber" },
  public: { label: "Public API", icon: faGlobe, tone: "sky" },
  secret: { label: "Server API", icon: faKey, tone: "violet" },
  general: { label: "General", icon: faLayerGroup, tone: "slate" },
};

export const HEALTH_LOOK: Record<HealthStatus, { tone: Tone; label: string }> = {
  healthy: { tone: "emerald", label: "Healthy" },
  unhealthy: { tone: "amber", label: "Degraded" },
  critical: { tone: "rose", label: "Critical" },
};

export const RUN_LOOK: Record<RunStatus, { tone: Tone; label: string; live?: boolean }> = {
  running: { tone: "sky", label: "Running", live: true },
  success: { tone: "emerald", label: "Success" },
  partial: { tone: "amber", label: "Partial" },
  failed: { tone: "rose", label: "Failed", live: true },
  crashed: { tone: "rose", label: "Crashed", live: true },
  skipped: { tone: "slate", label: "Skipped" },
  missed: { tone: "rose", label: "Missed", live: true },
  stuck: { tone: "rose", label: "Stuck", live: true },
};

export const CHECK_LOOK: Record<CheckStatus, { tone: Tone; label: string; live?: boolean }> = {
  healthy: { tone: "emerald", label: "Healthy" },
  degraded: { tone: "amber", label: "Degraded", live: true },
  down: { tone: "rose", label: "Down", live: true },
  unreachable: { tone: "rose", label: "Unreachable", live: true },
  "not-configured": { tone: "slate", label: "Not set up" },
};

export const BAD_RUN: RunStatus[] = ["failed", "crashed", "missed", "stuck"];

export const SERVER_ICON = faServer;
export const LIVE_ICON = faBolt;

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const pad = (value: string) => value.padStart(2, "0");

// ----------------------- CRON IN WORDS ------------------------------------
export function describeSchedule(schedule: string | null) {
  if (!schedule) return "No schedule registered";
  const parts = schedule.trim().split(/\s+/);
  const [minute, hour, day, month, weekday] = parts.length === 6 ? parts.slice(1) : parts;
  const isNumber = (value: string) => /^\d+$/.test(value);

  if (month !== "*") return schedule;
  if (hour === "*" && day === "*" && weekday === "*") {
    if (isNumber(minute)) return `Hourly at :${pad(minute)}`;
    if (minute.startsWith("*/") && 60 % Number(minute.slice(2)) === 0) return `Every ${minute.slice(2)} min`;
  }
  if (isNumber(minute) && isNumber(hour)) {
    const time = `${pad(hour)}:${pad(minute)}`;
    if (day === "*" && weekday === "*") return `Daily at ${time}`;
    if (isNumber(day) && weekday === "*") return `Day ${day} of each month at ${time}`;
    if (day === "*" && isNumber(weekday)) return `${WEEKDAYS[Number(weekday) % 7]}s at ${time}`;
  }
  return schedule;
}

// ----------------------- TIME RANGE TO START DATE ------------------------------------
const RANGE_MS: Record<string, number> = { "1h": 3600000, "24h": 86400000, "7d": 604800000, "30d": 2592000000 };
export const rangeStart = (range: string) => (RANGE_MS[range] ? new Date(Date.now() - RANGE_MS[range]).toISOString() : undefined);
